import { useParams } from 'react-router-dom'
import { useEvent } from '../hooks/useEvents'
import { usePresentationTimetable, useServiceInfo } from '../hooks/useServices'
import { formatEventDates } from '../lib/dates'
import { PrintShell, Section, Table, Empty, usePrintOnReady } from '../components/print/printKit'
import type { PresTimetableRow, EventServiceInfo } from '../types'

// ServiceTimetableDoc (Feature 2) — the browser-print "Timings & Set Plan" for a
// single event. Route /events/:id/service-timetable.
//
// ServiceTimetableBlock is presentational: it takes the already-resolved
// presentation-timetable rows + header info as props. The single-event page
// fetches them via hooks here; the all-events report (ServiceTimetableReport)
// feeds them from one batched endpoint. Both use the presentation timetable
// (manual Services, else the full per-day MERAC projection), so MERAC-bound
// events show their sessions rather than an empty table.

function fmtDate(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC'
  })
}

export function ServiceTimetableBlock({
  heading, subtitle, services, info
}: {
  heading?: string
  subtitle?: string
  services: PresTimetableRow[]
  info: EventServiceInfo | null
}) {
  const list = services
  const eventServices = list.filter((s) => s.serviceType !== 'pullman' && s.serviceType !== 'dining')
  const pullmanParallel = list.filter((s) => (s.serviceType === 'pullman' || s.serviceType === 'dining') && (s.pullmanMode ?? 'parallel') !== 'block')
  const hasDates = eventServices.some((s) => s.date)

  // In the all-events report, skip events with no timetable at all.
  if (heading && list.length === 0) return null

  const facts: { label: string; value: string | null | undefined }[] = [
    { label: 'Journey', value: info?.journey },
    { label: 'Run time', value: info?.runTime },
    { label: 'Set', value: info?.setFormation },
    { label: 'Capacity', value: info?.capacityNote }
  ]
  const hasFacts = facts.some((f) => f.value)
  const headers = hasDates
    ? ['Date', 'Dep.', 'Arr.', 'Service', 'Loco', 'From → To', 'Set', 'Cap.']
    : ['Dep.', 'Arr.', 'Service', 'Loco', 'From → To', 'Set', 'Cap.']

  return (
    <Section title={heading ?? 'Service timetable'}>
      {heading && subtitle && (
        <p style={{ fontSize: 12, color: '#5a5a5a', marginTop: -4 }}>{subtitle}</p>
      )}
      {hasFacts && (
        <p style={{ background: '#f5f2ea', padding: '6px 10px', borderRadius: 4 }}>
          {facts.filter((f) => f.value).map((f, i) => (
            <span key={f.label}>{i > 0 ? '  ·  ' : ''}<strong>{f.label}:</strong> {f.value}</span>
          ))}
        </p>
      )}
      {info?.notes && <p style={{ color: '#5a5a5a' }}>{info.notes}</p>}

      {eventServices.length === 0 ? (
        <Empty>No event services recorded.</Empty>
      ) : (
        <Table headers={headers}>
          {eventServices.map((s) => <Row key={s.id} s={s} showDate={hasDates} />)}
        </Table>
      )}

      {pullmanParallel.length > 0 && (
        <>
          <p style={{ fontWeight: 600, marginTop: 12 }}>Pullman / dining (running parallel)</p>
          <Table headers={headers}>
            {pullmanParallel.map((s) => <Row key={s.id} s={s} showDate={hasDates} />)}
          </Table>
        </>
      )}
    </Section>
  )
}

function Row({ s, showDate }: { s: PresTimetableRow; showDate: boolean }) {
  return (
    <tr>
      {showDate && <td style={{ whiteSpace: 'nowrap' }}>{s.date ? fmtDate(s.date) : '—'}</td>}
      <td style={{ fontFamily: 'monospace' }}>{s.timeText}</td>
      <td style={{ fontFamily: 'monospace', color: '#5a5a5a' }}>{s.arrivalTime ?? '—'}</td>
      <td><strong>{s.serviceLabel}</strong></td>
      <td>{s.loco ?? '—'}</td>
      <td>{s.originStation || s.destinationStation ? `${s.originStation ?? '—'} → ${s.destinationStation ?? '—'}` : '—'}</td>
      <td>{s.setFormation ?? '—'}</td>
      <td>{s.capacity ?? '—'}</td>
    </tr>
  )
}

export function ServiceTimetableDoc() {
  const { id: idParam } = useParams<{ id: string }>()
  const id = Number(idParam)
  const { data: event } = useEvent(id)
  const { data: timetable } = usePresentationTimetable(id)
  const { data: info } = useServiceInfo(id)
  usePrintOnReady(!!event && timetable != null)

  if (!event) return <PrintShell><p>Loading…</p></PrintShell>
  const when = formatEventDates(event.dates, event.startDate, event.endDate)

  return (
    <PrintShell>
      <section style={{ paddingBottom: 16 }}>
        <div style={{ fontSize: 12, color: '#5a5a5a', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>
          NYMR · Special Event Train Services — Timings &amp; Set Plan
        </div>
        <h1 style={{ fontSize: 32 }}>{event.name}</h1>
        <p style={{ fontSize: 14, color: '#5a5a5a' }}>{event.type.name} · {when}</p>
        <p style={{ fontSize: 11, color: '#5a5a5a', marginTop: 8 }}>Generated {new Date().toLocaleString('en-GB')}.</p>
      </section>
      <ServiceTimetableBlock services={timetable?.services ?? []} info={info ?? null} />
    </PrintShell>
  )
}
